import {
  IUserUploader,
  UploadLabelAndPhoneUserChange,
  UploadPermissionsUserChange,
  UploadUser,
  UploadUserDelete,
} from 'serviceWorker/repository/user/uploadUser';
import { __HttpRequestModel__ } from 'shared/domain/httpRequest/httpRequestModel';
import { UserInDto } from 'shared/domain/user/types/inDto';
import { UserEntity } from 'shared/domain/user/types/entity';
import {
  mergeUserInDtoIntoEntity,
  userEditApiResponsesToProjectUsers,
  applySentProjectPermissionToUserInDto,
  isEditedUser,
  toFlatUserInDtoOnlyResponse,
} from 'shared/domain/user/mapping/toEntity';
import { UserEditApiResponse } from 'shared/domain/user/types/userInDtoGuards';
import { broadcastAllUsers } from 'serviceWorker/broadcasts/user/all';
import { ensureAuthenticatedConfig } from 'serviceWorker/services/config';
import { createEntityMapById } from 'shared/utils/id/entityIds';
import {
  hasUserEditResponseData,
  extractUserIdFromEditRequestUrl,
  extractPrimaryUserIdFromEditResponse,
  toSingleUserInDto,
} from 'shared/domain/user/mapping/userEditResponse';
import {
  extractProjectPermissionFromPermissionsModel,
  NormalizeAndPersistUserOptions,
} from 'shared/domain/user/mapping/permissionsSnapshot';
import { debugLog } from 'shared/logger/debugLog';
import type { IHttpRequestRunner } from 'shared/domain/httpRequest/httpRequestRunner';
import {
  UserHttpCreateRequestModel,
  UserHttpDeleteRequestModel,
  UserHttpEditLabelAndPhoneRequestModel,
  UserHttpEditPermissionsRequestModel,
} from './types';

export class UserRequestRunner implements IHttpRequestRunner {
  constructor(
    private userUploader: IUserUploader,
    private uploadUserUseCase: UploadUser,
    private saveUsers: (data: UserEntity[]) => Promise<unknown>,
    private getUsersByIds: (ids: string[]) => Promise<UserEntity[]>,
    private uploadUserLabelAndPhoneChangesUseCase: UploadLabelAndPhoneUserChange,
    private uploadUserPermissionChangesUseCase: UploadPermissionsUserChange,
    private uploadUserDeleteUseCase: UploadUserDelete,
    private deleteUser: (userId: string) => Promise<unknown>
  ) {}
  async execute(request: __HttpRequestModel__): Promise<unknown> {
    switch (request.method) {
      case 'POST': {
        return this.uploadUser(request as UserHttpCreateRequestModel);
      }
      case 'PUT': {
        // TODO: should be separated
        if (request.data?.userEditPermissionsOutDto) {
          return this.uploadUserPermissionChanges(
            request as UserHttpEditPermissionsRequestModel
          );
        }
        return this.uploadUserPhoneAndLabelChanges(
          request as UserHttpEditLabelAndPhoneRequestModel
        );
      }
      case 'DELETE': {
        return this.uploadUserDelete(
          request as UserHttpDeleteRequestModel
        );
      }
    }
  }

  private async uploadUser(
    request: UserHttpCreateRequestModel
  ): Promise<UserInDto> {
    const response = await this.uploadUserUseCase(
      this.userUploader,
      request.data.userCreateOutDto,
      request.url
    );
    const projectContext = await this.getProjectContext();
    const sentProjectPermission = projectContext
      ? extractProjectPermissionFromPermissionsModel(
          request.data.userCreateOutDto.permissions,
          projectContext.projectId
        )
      : undefined;

    const normalized = await this.normalizeAndPersistUserEditResponse(
      response,
      {
        organizationId: projectContext?.organizationId,
        projectId: projectContext?.projectId,
        editedUserId: extractPrimaryUserIdFromEditResponse(response),
        sentProjectPermission,
      }
    );

    return toSingleUserInDto(normalized);
  }

  private async uploadUserPhoneAndLabelChanges(
    request: UserHttpEditLabelAndPhoneRequestModel
  ): Promise<UserInDto | UserInDto[]> {
    const usersInDto = await this.uploadUserLabelAndPhoneChangesUseCase(
      this.userUploader,
      request.data?.userLabelAndPhoneEditOutDto,
      request.url
    );
    const projectContext = await this.getProjectContext();
    return this.normalizeAndPersistUserEditResponse(usersInDto, {
      organizationId: projectContext?.organizationId,
      projectId: projectContext?.projectId,
      editedUserId: extractUserIdFromEditRequestUrl(request.url),
    });
  }

  private async uploadUserPermissionChanges(
    request: UserHttpEditPermissionsRequestModel
  ): Promise<UserInDto | UserInDto[]> {
    const usersInDto = await this.uploadUserPermissionChangesUseCase(
      this.userUploader,
      request.data.userEditPermissionsOutDto,
      request.url
    );
    const projectContext = await this.getProjectContext();
    const sentProjectPermission = projectContext
      ? extractProjectPermissionFromPermissionsModel(
          request.data.userEditPermissionsOutDto.permissions,
          projectContext.projectId
        )
      : undefined;

    if (!sentProjectPermission) {
      debugLog(
        'UserRequestRunner: could not extract sentProjectPermission from permission PUT payload',
        {
          projectId: projectContext?.projectId,
          permissions: request.data.userEditPermissionsOutDto.permissions,
        }
      );
    }

    return this.normalizeAndPersistUserEditResponse(usersInDto, {
      organizationId: projectContext?.organizationId,
      projectId: projectContext?.projectId,
      editedUserId: extractUserIdFromEditRequestUrl(request.url),
      sentProjectPermission,
      warnIfMissingSentProjectPermission: true,
    });
  }

  private async getProjectContext(): Promise<
    { projectId: string; organizationId?: string } | undefined
  > {
    const setup = await ensureAuthenticatedConfig();
    if (!setup?.projectId) {
      return undefined;
    }

    return {
      projectId: setup.projectId,
      organizationId: setup.organizationId,
    };
  }

  private async normalizeAndPersistUserEditResponse(
    response: UserEditApiResponse | UserEditApiResponse[],
    options: NormalizeAndPersistUserOptions = {}
  ): Promise<UserInDto | UserInDto[]> {
    if (!hasUserEditResponseData(response)) {
      return Array.isArray(response) ? [] : (response as UserInDto);
    }

    const context = await this.getProjectContext();
    const projectId = options.projectId ?? context?.projectId;
    const organizationId = options.organizationId ?? context?.organizationId;

    if (!projectId) {
      debugLog('UserRequestRunner: skip persist, projectId missing');
      return toFlatUserInDtoOnlyResponse(response);
    }

    // Fallback: if organizationId is missing (e.g., SW restart, old config),
    // still persist but role extraction may fall back to flat permissions.
    // This is safer than losing data entirely.
    const effectiveOrganizationId = organizationId ?? '';
    if (!organizationId) {
      debugLog(
        'UserRequestRunner: persisting without organizationId - role may be inaccurate until next sync'
      );
    }

    if (
      options.warnIfMissingSentProjectPermission &&
      !options.sentProjectPermission
    ) {
      debugLog(
        'UserRequestRunner: persisting permission edit without sentProjectPermission snapshot'
      );
    }

    const projectUsers = userEditApiResponsesToProjectUsers(
      effectiveOrganizationId,
      projectId,
      response
    );
    const existingUsers = await this.getUsersByIds(
      projectUsers.map((user) => user._id)
    );
    const existingUsersById = createEntityMapById(existingUsers);
    await this.saveUsers(
      projectUsers.map((userInDto) => {
        const projectPermission = isEditedUser(
          options.editedUserId,
          userInDto._id
        )
          ? options.sentProjectPermission
          : undefined;

        return mergeUserInDtoIntoEntity(
          existingUsersById.get(userInDto._id),
          userInDto,
          projectPermission
        );
      })
    );
    await broadcastAllUsers();

    const normalizedProjectUsers = projectUsers.map((userInDto) =>
      applySentProjectPermissionToUserInDto(userInDto, options)
    );

    return Array.isArray(response)
      ? normalizedProjectUsers
      : normalizedProjectUsers[0];
  }

  private async uploadUserDelete(
    request: UserHttpDeleteRequestModel
  ): Promise<boolean> {
    await this.uploadUserDeleteUseCase(this.userUploader, request.url);
    await this.deleteUser(request.data.userId);
    await broadcastAllUsers();
    return true;
  }
}
